import type { SpaceContact } from 'app/typings/core/common/contacts/contacts';

export type CoverageExperience = {
  id: number;
  categoryId: number;
  status: string;
  spaceIds?: number[] | null;
  isInPublishedPack: boolean;
};

export type SpaceContactSummaryExperience = {
  id: number;
  label: string;
};

export type SpaceContactSummary = {
  spaceId: number;
  spaceLabel: string;
  emails: string[];
  coveringExperiences: SpaceContactSummaryExperience[];
};

const PUBLISHED_STATUS = 'PUBLISHED';

// A space is "covered" only when it is linked to at least one experience that is
// both published and part of a published pack: that is the only case where a
// booking can actually include the experience and trigger a confirmation email.
const isCoveringExperience = (
  experience: CoverageExperience,
  spaceId: number
): boolean =>
  experience.status === PUBLISHED_STATUS &&
  experience.isInPublishedPack &&
  (experience.spaceIds ?? []).includes(spaceId);

const contactKey = (spaceId: number, email: string): string =>
  JSON.stringify([spaceId, email]);

type NewSpaceContact = SpaceContact & { spaceId: number; email: string };

const isCompleteContact = (contact: SpaceContact): contact is NewSpaceContact =>
  contact.spaceId !== undefined && !!contact.email;

// Only the emails the user just added matter: a (space, email) pair that was
// already configured is left out, the modal is about the new contacts' impact.
export const getNewSpacesContacts = (
  initial: SpaceContact[] | undefined,
  next: SpaceContact[] | undefined
): NewSpaceContact[] => {
  const initialKeys = new Set(
    (initial ?? [])
      .filter(isCompleteContact)
      .map((contact) => contactKey(contact.spaceId, contact.email))
  );

  return (next ?? [])
    .filter(isCompleteContact)
    .filter(
      (contact) => !initialKeys.has(contactKey(contact.spaceId, contact.email))
    );
};

export const buildSpacesContactsSummaries = ({
  spacesContacts,
  initialSpacesContacts,
  experiences,
  spaceLabelById,
  categoryNameById,
}: {
  spacesContacts: SpaceContact[];
  initialSpacesContacts: SpaceContact[];
  experiences: CoverageExperience[];
  spaceLabelById: Record<number, string>;
  categoryNameById: Record<number, string>;
}): SpaceContactSummary[] => {
  const summariesBySpaceId = new Map<number, SpaceContactSummary>();

  getNewSpacesContacts(initialSpacesContacts, spacesContacts).forEach(
    (contact) => {
      const existing = summariesBySpaceId.get(contact.spaceId);

      if (existing) {
        existing.emails.push(contact.email);

        return;
      }

      summariesBySpaceId.set(contact.spaceId, {
        spaceId: contact.spaceId,
        spaceLabel:
          spaceLabelById[contact.spaceId] ?? `Space #${contact.spaceId}`,
        emails: [contact.email],
        coveringExperiences: experiences
          .filter((experience) =>
            isCoveringExperience(experience, contact.spaceId)
          )
          .map((experience) => ({
            id: experience.id,
            label:
              categoryNameById[experience.categoryId] ??
              `Activity #${experience.id}`,
          })),
      });
    }
  );

  return Array.from(summariesBySpaceId.values());
};
